import React from 'react';
import { Helmet } from 'react-helmet-async';

const SEOHead = ({ 
  title = "LOVEYUHLIFE - Web Development & Consulting for Small Business | Logo Design, Website Development, Project Management",
  description = "LOVEYUHLIFE Inc specializes in comprehensive digital solutions for small businesses. Professional logo design, responsive website development, monitoring & analytics, and project management consulting services in Toronto, Canada.",
  keywords = "web development Toronto, small business website design, logo design services, project management consulting, digital marketing Toronto, responsive web design, business consulting Canada, website development Ontario, small business digital solutions, professional web services",
  image = "https://customer-assets.emergentagent.com/job_loveyuh-bizhelp/artifacts/agf3ac47_FullColor_BlackBackground1280x1024_72dpi.png",
  url = "https://loveyuhlife.ca",
  type = "website"
}) => {
  const structuredData = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Organization",
        "@id": `${url}/#organization`,
        "name": "LOVEYUHLIFE INC",
        "legalName": "LOVEYUHLIFE INC",
        "url": url,
        "logo": {
          "@type": "ImageObject",
          "url": image,
          "width": 1280,
          "height": 1024
        },
        "contactPoint": {
          "@type": "ContactPoint",
          "telephone": "+1-416-561-8684",
          "contactType": "customer service",
          "email": "admin@loveyuhlife.ca",
          "availableLanguage": "English"
        },
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "CA",
          "addressRegion": "Ontario",
          "addressLocality": "Toronto"
        },
        "sameAs": [],
        "foundingDate": "2021",
        "slogan": "Elevating Small Business Through Digital Innovation"
      },
      {
        "@type": "LocalBusiness",
        "@id": `${url}/#localbusiness`,
        "name": "LOVEYUHLIFE INC",
        "url": url,
        "telephone": "+1-416-561-8684",
        "email": "admin@loveyuhlife.ca",
        "address": {
          "@type": "PostalAddress",
          "addressCountry": "CA",
          "addressRegion": "Ontario",
          "addressLocality": "Toronto"
        },
        "geo": {
          "@type": "GeoCoordinates",
          "latitude": "43.6532",
          "longitude": "-79.3832"
        },
        "openingHoursSpecification": [
          {
            "@type": "OpeningHoursSpecification",
            "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
            "opens": "09:00",
            "closes": "18:00"
          }
        ],
        "priceRange": "$$",
        "servedCuisine": [],
        "hasOfferCatalog": {
          "@type": "OfferCatalog",
          "name": "Digital Services",
          "itemListElement": [
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Logo Design",
                "description": "Professional brand identity development and logo design services"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Website Development",
                "description": "Responsive website development and e-commerce solutions"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Monitoring & Analytics",
                "description": "Digital performance monitoring and analytics solutions"
              }
            },
            {
              "@type": "Offer",
              "itemOffered": {
                "@type": "Service",
                "name": "Project Management",
                "description": "Business operations and project management consulting"
              }
            }
          ]
        }
      },
      {
        "@type": "WebSite",
        "@id": `${url}/#website`,
        "url": url,
        "name": "LOVEYUHLIFE - Web Development & Consulting",
        "description": description,
        "publisher": {
          "@id": `${url}/#organization`
        },
        "potentialAction": [
          {
            "@type": "SearchAction",
            "target": `${url}/?s={search_term_string}`,
            "query-input": "required name=search_term_string"
          }
        ],
        "inLanguage": "en-CA"
      },
      {
        "@type": "WebPage",
        "@id": `${url}/#webpage`,
        "url": url,
        "name": title,
        "isPartOf": {
          "@id": `${url}/#website`
        },
        "about": {
          "@id": `${url}/#organization`
        },
        "description": description,
        "breadcrumb": {
          "@id": `${url}/#breadcrumb`
        },
        "inLanguage": "en-CA"
      }
    ]
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content="LOVEYUHLIFE INC" />
      <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
      <meta name="googlebot" content="index, follow" />
      <meta name="bingbot" content="index, follow" />
      
      {/* Language and Region */}
      <meta name="language" content="English" />
      <meta name="geo.region" content="CA-ON" />
      <meta name="geo.placename" content="Toronto" />
      <meta name="geo.position" content="43.6532;-79.3832" />
      <meta name="ICBM" content="43.6532, -79.3832" />
      
      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={url} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1280" />
      <meta property="og:image:height" content="1024" />
      <meta property="og:site_name" content="LOVEYUHLIFE" />
      <meta property="og:locale" content="en_CA" />
      
      {/* Twitter */}
      <meta property="twitter:card" content="summary_large_image" />
      <meta property="twitter:url" content={url} />
      <meta property="twitter:title" content={title} />
      <meta property="twitter:description" content={description} />
      <meta property="twitter:image" content={image} />
      
      {/* Business Information */}
      <meta name="contact" content="admin@loveyuhlife.ca" />
      <meta name="phone" content="+1-416-561-8684" />
      <meta name="business:contact_data:street_address" content="Toronto, Ontario" />
      <meta name="business:contact_data:locality" content="Toronto" />
      <meta name="business:contact_data:region" content="Ontario" />
      <meta name="business:contact_data:postal_code" content="" />
      <meta name="business:contact_data:country_name" content="Canada" />
      
      {/* Additional SEO Tags */}
      <meta name="distribution" content="global" />
      <meta name="rating" content="general" />
      <meta name="revisit-after" content="1 days" />
      <meta name="theme-color" content="#ff6b4a" />
      <meta name="msapplication-navbutton-color" content="#ff6b4a" />
      <meta name="apple-mobile-web-app-status-bar-style" content="#ff6b4a" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={url} />
      
      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

export default SEOHead;